import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, EmailAuthProvider, linkWithCredential, linkWithPopup, onAuthStateChanged, signInAnonymously, createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup, signOut, getAdditionalUserInfo, sendPasswordResetEmail, reauthenticateWithCredential, reauthenticateWithPopup } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, updateDoc, onSnapshot, serverTimestamp, arrayUnion, deleteDoc, FieldValue, runTransaction, writeBatch } from 'firebase/firestore';
import { getStorage } from "firebase/storage";
import { getAnalytics, logEvent as firebaseLogEvent, setUserProperties as firebaseSetUserProperties } from "firebase/analytics";
import { getFunctions } from 'firebase/functions';
import logger from '../utils/logger';

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Validate required environment variables
const requiredEnvVars = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID',
    'REACT_APP_FIREBASE_MEASUREMENT_ID'
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);

if (missingVars.length > 0) {
    console.warn('Missing required environment variables:', missingVars);
    console.warn('Falling back to mock authentication mode for local development');
    
    // Check if we're in development mode
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isDevelopment) {
        // Enable mock authentication for development
        localStorage.setItem('speak-roots-use-mock-auth', 'true');
        localStorage.setItem('speak-roots-use-real-auth', 'false');
        console.log('\u2705 Mock authentication enabled for development');
    } else {
        // In production, this is a real error
        throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
    }
}

// Initialize Firebase and its services
let app, auth, db, storage, analytics, functions;

try {
    // Check if Firebase app is already initialized
    if (getApps().length === 0) {
        app = initializeApp(firebaseConfig);
    } else {
        app = getApps()[0];
    }

    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    functions = getFunctions(app);
    
    // Check if we're in production and enable real authentication
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isDevelopment) {
        localStorage.setItem('speak-roots-use-mock-auth', 'false');
        localStorage.setItem('speak-roots-use-real-auth', 'true');
        console.log('\u2705 Real authentication enabled for production');
    }
} catch (error) {
    console.warn('Firebase initialization failed:', error);
    
    // Check if we're in development mode
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isDevelopment) {
        console.warn('Falling back to mock mode for local development');
        // Enable mock authentication for development
        localStorage.setItem('speak-roots-use-mock-auth', 'true');
        localStorage.setItem('speak-roots-use-real-auth', 'false');
    } else {
        logger.error('App', 'Firebase initialization failed in production:', error);
        // In production, enable real authentication even if Firebase fails
        localStorage.setItem('speak-roots-use-mock-auth', 'false');
        localStorage.setItem('speak-roots-use-real-auth', 'true');
    }
}

// Initialize Analytics with proper error handling
try {
    if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
        analytics = getAnalytics(app);
        logger.info('FIREBASE', 'Info');
        logger.info('FIREBASE', 'Info', analytics);
        
        // Check if analytics is ready after a brief delay
        setTimeout(() => {
            logger.info('FIREBASE', 'Info', !!analytics?.logEvent);
            logger.info('FIREBASE', 'Info', !!firebaseLogEvent);
        }, 100);
    } else {
        logger.warn('FIREBASE', 'Warning');
        analytics = null;
    }
} catch (error) {
    logger.error('FIREBASE', 'Error', error);
    analytics = null;
}

// Development configuration for localhost
if (window.location.hostname === 'localhost') {
    console.log('Running in development mode on localhost');
    console.log('Firebase config:', firebaseConfig);
    console.log('Current URL:', window.location.href);
}
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({
    prompt: 'select_account'
});

// Analytics ready state
let isAnalyticsReady = false;

// Check analytics readiness
const checkAnalyticsReady = () => {
    if (analytics && firebaseLogEvent && typeof firebaseLogEvent === 'function') {
        isAnalyticsReady = true;
        logger.info('FIREBASE', 'Info');
        return true;
    }
    return false;
};

// Enhanced Analytics helper
export const trackEvent = (eventName, params = {}) => {
    try {
        logger.info('ANALYTICS', 'Info');
        
        // Check if analytics is ready
        if (!isAnalyticsReady) {
            isAnalyticsReady = checkAnalyticsReady();
        }
        
        logger.info('ANALYTICS', 'Info', isAnalyticsReady);
        logger.info('ANALYTICS', 'Info', !!analytics);
        logger.info('ANALYTICS', 'Info', !!firebaseLogEvent);
        
        if (isAnalyticsReady && analytics && firebaseLogEvent) {
            // Send to Firebase Analytics using the imported function
            firebaseLogEvent(analytics, eventName, {
                timestamp: new Date().toISOString(),
                page_url: window.location.href,
                page_title: document.title,
                ...params
            });
            logger.info('ANALYTICS', 'Info', params || '');
        } else {
            logger.warn('ANALYTICS', 'Warning', params || '');
            logger.warn('ANALYTICS', 'Warning', isAnalyticsReady);
            logger.warn('ANALYTICS', 'Warning', analytics);
            logger.warn('ANALYTICS', 'Warning', firebaseLogEvent);
        }
        
        // Always log for debugging
        logger.info('ANALYTICS', 'Info', params || '');
    } catch (error) {
        logger.error('ANALYTICS', 'Error', error);
    }
};

// Set user properties
export const setUserProperties = (properties) => {
    try {
        if (analytics && firebaseSetUserProperties) {
            firebaseSetUserProperties(analytics, properties);
            logger.info('ANALYTICS', 'Info', properties);
        } else {
            logger.warn('ANALYTICS', 'Warning', properties);
        }
        logger.info('ANALYTICS', 'Info', properties);
    } catch (error) {
        logger.error('ANALYTICS', 'Error', error);
    }
};

// Track user engagement
export const trackUserEngagement = (engagementTime, sessionId) => {
    try {
        if (analytics && firebaseLogEvent) {
            firebaseLogEvent(analytics, 'user_engagement', {
                engagement_time_msec: engagementTime,
                session_id: sessionId
            });
        }
    } catch (error) {
        logger.error('ANALYTICS', 'Error', error);
    }
};

// Expose all necessary Firebase services and providers
export {
    auth, db, storage, analytics, functions, googleProvider, GoogleAuthProvider,
    EmailAuthProvider, linkWithCredential, linkWithPopup, signInWithPopup,
    onAuthStateChanged, signInAnonymously, createUserWithEmailAndPassword,
    signInWithEmailAndPassword, signOut, getAdditionalUserInfo,
    sendPasswordResetEmail, reauthenticateWithCredential, reauthenticateWithPopup, doc, setDoc, getDoc, updateDoc, onSnapshot,
    serverTimestamp, arrayUnion, deleteDoc, FieldValue, runTransaction, writeBatch
};

// Export the appId separately for use in other files
export const appId = firebaseConfig.projectId;
